/**
 * The bar that says "your email address is not verified yet".
 *
 * An email + password signup gets ONE verification link, at signup, and is let
 * into the dashboard without opening it. Nothing told them it mattered, and
 * nothing could send another: on 2026-10-24 a prd signup opened that link three
 * and a half hours later, after its one-hour lifetime, and got "Your email
 * address could not be verified." with no way forward. Accepting an invitation
 * requires a verified email, so that account could never join a team.
 *
 * The marker is the id token's standard `email_verified` claim, strictly
 * `false` — social and enterprise logins carry their IdP's verdict, and a
 * session without the claim is not assumed unverified. Renders nothing
 * otherwise, and nothing in an impersonated session: the server refuses to
 * mail the customer from one, and the operator should not be nagged about the
 * customer's inbox.
 *
 * The claim is as old as the session. A link opened in another tab leaves this
 * bar up until the next sign-in, which is why "already verified" offers one.
 */
import * as React from "react";
import { MailWarning } from "lucide-react";
import { getImpersonationState } from "@authe/fe-commons/auth";
import type { AutheClaims } from "@authe/fe-commons/auth";
import { useSession } from "@authe/ui-core/session";
import { useVerificationEmailSource } from "@/api/adapters/account";
import type { VerificationEmailOutcome } from "@/api/verificationEmailSource";

type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "done"; outcome: VerificationEmailOutcome }
  | { kind: "failed"; message: string };

/** "in 3 min" / "in a minute" for a retry hint. */
function retryPhrase(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  return minutes <= 1 ? "in a minute" : `in ${minutes} min`;
}

/** What the bar says after a send, in words. */
export function outcomeMessage(outcome: VerificationEmailOutcome, fallbackEmail: string | null): string {
  if (outcome.status === "already_verified") {
    return "Your email address is verified. Sign in again to clear this notice.";
  }
  const to = outcome.email ?? fallbackEmail;
  const where = to ? ` to ${to}` : "";
  if (outcome.status === "recently_sent") {
    return `A verification link was sent${where} moments ago. Check your inbox and spam folder, or ask again ${retryPhrase(outcome.retryAfterSeconds)}.`;
  }
  return `A new verification link is on its way${where}. Open it, then sign in again.`;
}

export function EmailVerificationBanner(): React.ReactElement | null {
  const { session, login } = useSession();
  const source = useVerificationEmailSource();
  const [state, setState] = React.useState<SendState>({ kind: "idle" });
  const [dismissed, setDismissed] = React.useState(false);
  const bannerRef = React.useRef<HTMLDivElement>(null);

  const unverified = session?.idClaims?.email_verified === false;
  const impersonated = getImpersonationState(session?.claims as AutheClaims | undefined).active;
  const visible = unverified && !impersonated && !dismissed;

  // The shell sizes itself to the viewport minus the bars above it (see
  // styles/layout.css), so the page does not scroll a second viewport under them.
  React.useEffect(() => {
    const banner = bannerRef.current;
    if (!visible || !banner) return;
    const measure = () => document.documentElement.style.setProperty(
      "--verification-banner-height", `${banner.getBoundingClientRect().height}px`,
    );
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(banner);
    return () => { observer?.disconnect(); document.documentElement.style.removeProperty("--verification-banner-height"); };
  }, [visible]);

  if (!visible) return null;

  const email = session?.user.email ?? null;
  const send = async () => {
    setState({ kind: "sending" });
    try {
      setState({ kind: "done", outcome: await source.send() });
    } catch (err) {
      setState({ kind: "failed", message: err instanceof Error && err.message ? err.message : "The email could not be sent." });
    }
  };
  const verifiedSince = state.kind === "done" && state.outcome.status === "already_verified";

  return (
    <div
      ref={bannerRef}
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-muted px-4 py-1.5 text-xs text-foreground print:hidden"
    >
      <MailWarning className="size-3.5 shrink-0" aria-hidden />
      <span aria-live="polite">
        {state.kind === "done" ? (
          outcomeMessage(state.outcome, email)
        ) : state.kind === "failed" ? (
          <>Could not send a verification link: {state.message}</>
        ) : (
          <>
            Verify your email address{email ? <> (<strong>{email}</strong>)</> : null} to accept invitations
            to a team. Your signup link may have expired.
          </>
        )}
      </span>
      <span className="ml-auto flex items-center gap-3">
        {verifiedSince ? (
          <button
            type="button"
            className="font-medium underline underline-offset-2 hover:no-underline"
            onClick={() => void login({ returnTo: window.location.href })}
          >
            Sign in again
          </button>
        ) : state.kind !== "done" ? (
          <button
            type="button"
            className="font-medium underline underline-offset-2 hover:no-underline disabled:opacity-60"
            disabled={state.kind === "sending"}
            onClick={() => void send()}
          >
            {state.kind === "sending" ? "Sending…" : state.kind === "failed" ? "Try again" : "Send a new link"}
          </button>
        ) : null}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setDismissed(true)}
        >
          {state.kind === "done" ? "Close" : "Later"}
        </button>
      </span>
    </div>
  );
}
